import { isRevenueCatConfigured, purchaseSurface } from '../../lib/revenuecat'
import AndroidPlayPurchasePanel from './AndroidPlayPurchasePanel'
import ProPurchasePanel from './ProPurchasePanel'
import ProUpgradeModal from './ProUpgradeModal'

/**
 * The paywall sheet, wired exactly as DeckChat wires it.
 *
 * `key={userId}` so an identity change REMOUNTS the purchase panel and no
 * render of it can outlive the user it was built for. With no RevenueCat key,
 * or no signed-in user, the modal falls back to its static plans and no
 * RevenueCat bytes are ever fetched.
 *
 * EXTRACTED from SubscriptionCard on 2026-08-28, unchanged. It was private to
 * the account page while the account page was one of only two routes to the
 * paywall; the nav menu and Home's band are two more, and three copies of
 * "when may this actually sell" is three chances to get the RevenueCat
 * preconditions subtly different.
 *
 * Inside a billing-capable Android app build (purchaseSurface() 'play') the
 * slot is AndroidPlayPurchasePanel instead, decided HERE before anything
 * else mounts, so no web offering effect ever runs in the app. It mounts
 * whenever the sheet is open, signed in or not: a signed-out or guest
 * reader gets its "Sign in to subscribe" rather than static web prices.
 * Deck Chat and Set Check mount this same component, so every entry point
 * shares the one decision.
 */
export default function Paywall({
  open,
  onClose,
  userId,
  token,
  isPro,
  proMonthlyMessages,
  finishOffered,
  entry,
}: {
  open: boolean
  onClose: () => void
  userId: string | null
  token: string | null
  isPro: boolean
  /**
   * The Pro tier's monthly cap off /pro/status (`pro_cap_month`), threaded
   * by callers that already hold a status. Deliberately a PROP and not a
   * `useProStatus()` call in here: WatchtowerPage mounts this wrapper with
   * no QueryClient at all, and a hook here would drag react-query into
   * every page that can open a paywall. Undefined renders the copy fallback.
   */
  proMonthlyMessages?: number
  /**
   * The caller opts in to the Finish my deck copy (proStatus
   * finishOfferedByStatus, or Finish's own sheet). Absent: not offered.
   * Passed through to ProUpgradeModal, which gates the Finish copy on it.
   */
  finishOffered?: boolean
  /**
   * Which entry point opened the sheet (deck_chat, account, set_check,
   * watchtower, home_band, account_menu, coach). Passed to the native Play
   * paywall; unused by the web checkout.
   */
  entry?: string
}) {
  const surface = purchaseSurface()
  // eslint-disable-next-line i18next/no-literal-string -- purchase surface code
  const play = open && surface === 'play'
  // eslint-disable-next-line i18next/no-literal-string -- purchase surface code
  const canPurchase = open && !!userId && !!token && isRevenueCatConfigured() && surface === 'web'
  return (
    <ProUpgradeModal
      open={open}
      onClose={onClose}
      proMonthlyMessages={proMonthlyMessages}
      finishOffered={finishOffered}
      purchaseSlot={
        play ? (
          <AndroidPlayPurchasePanel
            // eslint-disable-next-line i18next/no-literal-string -- a React key
            key={userId ?? 'signed-out'}
            userId={userId}
            token={token}
            isPro={isPro}
            // eslint-disable-next-line i18next/no-literal-string -- entry code
            entry={entry ?? 'paywall'}
          />
        ) : canPurchase ? (
          <ProPurchasePanel
            key={userId!}
            userId={userId!}
            token={token!}
            isPro={isPro}
            onClose={onClose}
          />
        ) : undefined
      }
    />
  )
}
